import React, { useState, useCallback } from "react";
import { Collapse } from "reactstrap";
import { useNavigate } from "react-router-dom";
import '../css/ClientContracts.css'
import { useCurrencyFormatter } from '../../../Components/Hooks/useCurrencyFormatter';
import MoneyDisplay from '../../Contract/ContractDetail/Components/MoneyDisplay';
import { PAYMENT_TYPE_LABELS } from '../../../common/paymentTypes';

const GREEN = "#1a9c5f";
// Two distinct indigos: a brighter one for payment-status dots, the page's
// own darker accent for pill/badge text.
const DOT_INDIGO = "#6366f1";
const ACCENT_INDIGO = "#4338ca";
const GREY = "#c3c3cf";
// Overdue isn't part of the design's Paid/Upcoming/Unscheduled model (its
// sample data has no past-due unpaid payment to show), but real contracts
// do fall behind — flagging it red rather than showing a silently-wrong
// "Upcoming" is a deliberate, disclosed addition, not a mismatch.
const RED = "#e5484d";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getDate()} ${date.toLocaleDateString("en-US", { month: "short" })} ${date.getFullYear()}`;
};

// Binary per the data model (In Progress | Completed) — a brand-new,
// zero-paid contract is still "In Progress", not a third state.
const getContractStatus = (contract) => {
  const total = contract.total_contract_value || 0;
  const paid = contract.total_cashed || 0;
  const completed = total > 0 && paid >= total;
  return completed
    ? { text: "Completed", bg: "#e6f4ec", fg: "#146c43" }
    : { text: "In Progress", bg: "#eceafd", fg: ACCENT_INDIGO };
};

const getProgressInfo = (contract) => {
  const total = contract.total_contract_value || 0;
  const paid = contract.total_cashed || 0;
  const pct = total > 0 ? Math.round((paid / total) * 100) : 0;
  const nextUnpaid = (contract.payments || [])
    .filter((p) => !p.cashed)
    .sort((a, b) => {
      if (!a.booking_date) return 1;
      if (!b.booking_date) return -1;
      return new Date(a.booking_date) - new Date(b.booking_date);
    })[0];
  return {
    pct,
    nextLine: nextUnpaid
      ? `next ${nextUnpaid.booking_date ? formatDate(nextUnpaid.booking_date) : "unscheduled"}`
      : "fully settled",
  };
};

// One row per payment, in due-date order, with the visual bits (dot color,
// state pill, "Down Payment 1"/"2" suffix when a contract has more than one
// of the same type) the timeline + donut legend both need.
const buildPaymentRows = (payments, totalValue) => {
  const sorted = [...(payments || [])].sort((a, b) => {
    if (!a.booking_date) return 1;
    if (!b.booking_date) return -1;
    return new Date(a.booking_date) - new Date(b.booking_date);
  });

  const typeCounts = {};
  sorted.forEach((p) => {
    typeCounts[p.type] = (typeCounts[p.type] || 0) + 1;
  });
  const typeSeen = {};
  const now = new Date();

  return sorted.map((p) => {
    typeSeen[p.type] = (typeSeen[p.type] || 0) + 1;
    const baseLabel = PAYMENT_TYPE_LABELS[p.type] || p.type;
    const name = typeCounts[p.type] > 1 ? `${baseLabel} ${typeSeen[p.type]}` : baseLabel;

    const isPaid = !!p.cashed;
    const isOverdue = !isPaid && !!p.booking_date && new Date(p.booking_date) < now;
    const isUpcoming = !isPaid && !isOverdue && !!p.booking_date;
    const state = isPaid ? "Paid" : isOverdue ? "Overdue" : isUpcoming ? "Upcoming" : "Unscheduled";
    const dot = isPaid ? GREEN : isOverdue ? RED : isUpcoming ? DOT_INDIGO : GREY;

    let due;
    if (isPaid) {
      due = `Paid ${formatDate(p.cashed_date || p.booking_date)}`;
    } else if (p.booking_date) {
      due = `${isOverdue ? "Overdue" : "Due"} ${formatDate(p.booking_date)}`;
    } else {
      due = "No due date set";
    }

    return {
      id: p.id,
      name,
      amount: p.amount,
      amountRon: p.amount_ron,
      due,
      state,
      dot,
      dueFg: isOverdue ? RED : isUpcoming ? "#3c3c46" : "#8a8a96",
      pillBg: isPaid ? "#e6f4ec" : isOverdue ? "#fdecea" : isUpcoming ? "#eceafd" : "#f2f2f6",
      pillFg: isPaid ? "#146c43" : isOverdue ? RED : isUpcoming ? ACCENT_INDIGO : "#8a8a96",
      invoiceLabel: p.invoice ? `${p.invoice.series_name}/${p.invoice.number}` : null,
      share: totalValue > 0 ? ((p.amount / totalValue) * 100).toFixed(1) : "0.0",
    };
  });
};

const buildDonutGradient = (rows) => {
  let acc = 0;
  const stops = rows.map((r) => {
    const from = acc;
    acc += parseFloat(r.share) || 0;
    return `${r.dot} ${from.toFixed(2)}% ${acc.toFixed(2)}%`;
  });
  return `conic-gradient(${stops.join(",")})`;
};

// Each card expands independently — several can be open at once — and the
// first contract is open by default.
const ClientContracts = ({ contracts }) => {
  const navigate = useNavigate();
  const [openIndexes, setOpenIndexes] = useState(() => new Set([0]));
  const { formatCurrency } = useCurrencyFormatter();

  const toggle = useCallback((index) => {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const handleViewContract = useCallback((contractId, e) => {
    e.stopPropagation();
    navigate(`/financial/contracts/${contractId}`);
  }, [navigate]);

  if (!contracts || contracts.length === 0) {
    return (
      <div className="text-center p-5">
        <div className="mb-4">
          <div
            className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
            style={{ width: "80px", height: "80px" }}
          >
            <i className="ri-file-list-3-line fs-2 text-muted"></i>
          </div>
        </div>
        <h5 className="mb-2">No Contracts Available</h5>
        <p className="text-muted mb-0">
          There are currently no contracts associated with this client.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%" }}>
      {contracts.map((contract, index) => {
        const isOpen = openIndexes.has(index);
        const hasPayments = contract.payments?.length > 0;
        const rows = hasPayments ? buildPaymentRows(contract.payments, contract.total_contract_value || 0) : [];
        const unpaidCount = rows.filter((r) => r.state !== "Paid").length;
        const isRon = contract.currency === "RON";
        const statusBadge = getContractStatus(contract);
        const progressInfo = getProgressInfo(contract);
        const outstandingBalance = (contract.total_contract_value || 0) - (contract.total_cashed || 0);
        const outstandingBalanceRon =
          isRon && contract.total_contract_value_ron != null && contract.total_cashed_ron != null
            ? contract.total_contract_value_ron - contract.total_cashed_ron
            : null;

        return (
          <div
            key={contract.id}
            style={{ background: "#fff", border: "1px solid #e6e6ec", borderRadius: 14, overflow: "hidden" }}
          >
            <div
              className="contract-header"
              onClick={() => toggle(index)}
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "14px 16px",
                padding: "16px 18px",
              }}
            >
              <div
                style={{
                  flex: "none",
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "#f2f2f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, color: "#63636f" }}>
                  {isOpen ? "▲" : "▼"}
                </span>
              </div>

              <div style={{ minWidth: 0, flex: "1 1 200px", display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                <span style={{ fontSize: 15.5, fontWeight: 650 }}>Contract #{contract.pvc_number}</span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    padding: "3px 8px",
                    borderRadius: 5,
                    background: statusBadge.bg,
                    color: statusBadge.fg,
                  }}
                >
                  {statusBadge.text}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 16, flex: "1 1 auto", justifyContent: "flex-end", minWidth: 0 }}>
                {hasPayments && (
                  <div style={{ flex: "0 1 190px", minWidth: 120 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#63636f", marginBottom: 5 }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {formatCurrency(contract.total_cashed || 0, "EUR", true)} paid
                      </span>
                      <span style={{ fontWeight: 600, flexShrink: 0, marginLeft: 6 }}>{progressInfo.pct}%</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: "#eceaf1", overflow: "hidden" }}>
                      <div style={{ height: "100%", borderRadius: 3, background: GREEN, width: `${progressInfo.pct}%` }} />
                    </div>
                  </div>
                )}

                <div style={{ flex: "none", textAlign: "right" }}>
                  <MoneyDisplay
                    eurAmount={contract.total_contract_value}
                    ronAmount={isRon ? contract.total_contract_value_ron : null}
                    size={16}
                  />
                  <div style={{ fontSize: 12, color: "#8a8a96", whiteSpace: "nowrap", marginTop: 2 }}>
                    {hasPayments ? progressInfo.nextLine : "fully settled"}
                  </div>
                </div>

                <button
                  type="button"
                  className="contract-details-link"
                  onClick={(e) => handleViewContract(contract.id, e)}
                  style={{
                    flex: "none",
                    font: "inherit",
                    fontSize: 12.5,
                    fontWeight: 600,
                    padding: "8px 12px",
                    border: "1px solid #dcdce3",
                    borderRadius: 8,
                    color: "#1b1b21",
                    background: "#fff",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Details
                </button>
              </div>
            </div>

            <Collapse isOpen={isOpen}>
              {hasPayments ? (
                <div style={{ borderTop: "1px solid #eeeef2", background: "#fbfbfc", display: "flex", flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 340px", minWidth: 0, padding: "20px 22px 22px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                      <span style={{ fontSize: 12, fontWeight: 650, letterSpacing: 0.7, textTransform: "uppercase", color: "#63636f" }}>
                        Payment schedule
                      </span>
                      <span style={{ fontSize: 12, color: "#8a8a96" }}>
                        {unpaidCount} of {rows.length} remaining
                      </span>
                    </div>

                    {rows.map((r, i) => (
                      <div
                        key={r.id || i}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "22px minmax(0,1fr) auto",
                          gap: 14,
                          alignItems: "start",
                          paddingBottom: 18,
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", alignSelf: "stretch" }}>
                          <div
                            style={{
                              width: 13,
                              height: 13,
                              borderRadius: "50%",
                              background: r.dot,
                              border: "2.5px solid #fbfbfc",
                              boxShadow: `0 0 0 1.5px ${r.dot}`,
                              marginTop: 3,
                              flex: "none",
                            }}
                          />
                          <div style={{ width: 1.5, flex: 1, background: "#e4e4ea", marginTop: 5 }} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600 }}>{r.name}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", marginTop: 4 }}>
                            <span style={{ fontSize: 12.5, color: r.dueFg }}>{r.due}</span>
                            <span
                              style={{
                                fontSize: 10.5,
                                fontWeight: 600,
                                letterSpacing: 0.4,
                                textTransform: "uppercase",
                                padding: "2.5px 7px",
                                borderRadius: 5,
                                background: r.pillBg,
                                color: r.pillFg,
                              }}
                            >
                              {r.state}
                            </span>
                            {r.invoiceLabel && (
                              <span style={{ fontSize: 12.5, fontWeight: 600, color: ACCENT_INDIGO }}>{r.invoiceLabel}</span>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <MoneyDisplay eurAmount={r.amount} ronAmount={isRon ? r.amountRon : null} size={14.5} />
                          <div style={{ fontSize: 12, color: "#8a8a96", marginTop: 2 }}>{r.share}%</div>
                        </div>
                      </div>
                    ))}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderTop: "1px solid #eeeef2",
                        paddingTop: 13,
                        marginTop: -4,
                      }}
                    >
                      <span style={{ fontSize: 13, color: "#63636f" }}>Outstanding balance</span>
                      <MoneyDisplay eurAmount={outstandingBalance} ronAmount={outstandingBalanceRon} size={15} />
                    </div>
                  </div>

                  <div
                    style={{
                      flex: "1 1 260px",
                      minWidth: 260,
                      borderLeft: "1px solid #eeeef2",
                      padding: "20px 22px 22px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    <span
                      style={{
                        alignSelf: "flex-start",
                        fontSize: 12,
                        fontWeight: 650,
                        letterSpacing: 0.7,
                        textTransform: "uppercase",
                        color: "#63636f",
                      }}
                    >
                      Distribution
                    </span>
                    <div
                      style={{
                        position: "relative",
                        width: 168,
                        height: 168,
                        borderRadius: "50%",
                        background: buildDonutGradient(rows),
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          inset: 26,
                          borderRadius: "50%",
                          background: "#fbfbfc",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <div style={{ fontSize: 21, fontWeight: 650, color: "#146c43" }}>{progressInfo.pct}%</div>
                        <div style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "#8a8a96", marginTop: 1 }}>
                          settled
                        </div>
                      </div>
                    </div>
                    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 9 }}>
                      {rows.map((r, i) => (
                        <div key={r.id || i} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                          <div style={{ width: 9, height: 9, borderRadius: 2, background: r.dot, flex: "none" }} />
                          <span
                            style={{
                              fontSize: 12.5,
                              color: "#3c3c46",
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r.name}
                          </span>
                          <span style={{ fontSize: 12.5, fontWeight: 600, color: "#63636f", fontVariantNumeric: "tabular-nums" }}>
                            {r.share}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ borderTop: "1px solid #eeeef2", background: "#fbfbfc", padding: "20px 22px" }}>
                  <span className="text-muted" style={{ fontSize: 13 }}>
                    No payment data available for this contract yet.
                  </span>
                </div>
              )}
            </Collapse>
          </div>
        );
      })}
    </div>
  );
};

export default ClientContracts;
